import { computeMockupSupplementKey } from "./flightOfferAdapter.js";
import { requireLdsSupplementCatalogId } from "../../../utils/lds/ldsSupplementCatalogId.js";

function normalizeLdsSupplementParams(params) {
  if (params == null) return [];
  if (Array.isArray(params)) return params;
  if (typeof params === "object" && params.name != null) return [params];
  return [];
}

function normalizeLinkedFlightsFlightArray(linkedFlights) {
  const f = linkedFlights?.flight;
  if (f == null) return [];
  return Array.isArray(f) ? f : [f];
}

function firstLegFromFlightNode(flightNode) {
  if (!flightNode?.leg) return null;
  const L = flightNode.leg;
  return Array.isArray(L) ? L[0] : L;
}

function isCheckedForPaxAndKey(extras, supplementKey, pax) {
  const id = pax?._mockPaxId;
  if (!id) return false;
  const arr = extras?.supplementPaxSelections?.[supplementKey];
  return Array.isArray(arr) && arr.includes(id);
}

/**
 * מבנה כמו לאחר initLdsSupplements + עדכון checked לפי supplementPaxSelections במוקאב.
 * @param {object} opts
 * @param {object[]} opts.mergedRoomsAndPaxInfo
 * @param {object} opts.linkedFlights
 * @param {object|object[]|null} opts.rawSupplements — deal.supplement
 * @param {object} opts.extras — { supplementPaxSelections }
 * @param {(fareUsd: number, supplementRow?: object) => number} opts.computeOurFareNis
 */
export function buildLdsSupplementObjectForProposalSave({
  mergedRoomsAndPaxInfo,
  linkedFlights,
  rawSupplements,
  extras,
  computeOurFareNis,
}) {
  let supList = rawSupplements;
  if (supList == null) return {};
  if (typeof supList === "object" && !Array.isArray(supList)) {
    supList = [supList];
  }
  const filtered = supList.filter((supp) => {
    const t = supp?.type || supp?.Type;
    return t === "TROLLEY" || t === "LUGGAGE" || t === "BAGGAGE";
  });

  const parsedSupplement = filtered.map((item) => {
    const params = normalizeLdsSupplementParams(item.params);
    const wEntry = params.find((param) => param.name === "weight");
    const uEntry = params.find((param) => param.name === "unit");
    const catalogId = requireLdsSupplementCatalogId(
      item,
      `supplement type=${item?.type || item?.Type || "?"}`
    );
    return {
      raw: item,
      "@seqNum": item["@seqNum"],
      id: catalogId,
      displayedpackageid: item.displayedpackageid,
      type: (item.type || item.Type) === "BAGGAGE" ? "LUGGAGE" : item.type || item.Type,
      description: item.desc,
      category: item.category,
      fare: parseFloat(item.fare),
      weight: wEntry?.value ? parseFloat(wEntry.value) : null,
      unit: uEntry?.value || null,
      fltRef: item.fltRef,
      paxRef: item.paxRef,
    };
  });

  const ldsSupplement = {};
  let suppCount = 0;
  const flights = normalizeLinkedFlightsFlightArray(linkedFlights);
  const flightsForSupplements =
    flights.length > 0 ? flights : [{ leg: { depCity: "", arrCity: "" } }];

  let itemLoopIndex = 0;
  parsedSupplement.forEach((supplementRow) => {
    const rawItem = supplementRow.raw;
    const { raw: _rawDrop, ...suppForOrig } = supplementRow;
    const currentI = itemLoopIndex;
    itemLoopIndex += 1;

    mergedRoomsAndPaxInfo.forEach((roomBlock) => {
      const paxList = [...(roomBlock.adults || []), ...(roomBlock.kids || [])];
      paxList.forEach((pax) => {
        if (String(supplementRow.fltRef) === "-3") {
          flightsForSupplements.forEach((flightNode, flightIndex) => {
            const leg = firstLegFromFlightNode(flightNode) || {};
            suppCount += 1;
            const seqNum = suppCount;
            const supplementKey = computeMockupSupplementKey(
              rawItem,
              flightIndex,
              currentI
            );
            ldsSupplement[seqNum] = {
              id: supplementRow.id,
              displayedpackageid: supplementRow.displayedpackageid,
              "@seqNum": seqNum,
              orig: {
                flight: flightNode.leg,
                supp: suppForOrig,
              },
              pax,
              type: supplementRow.type,
              checked: isCheckedForPaxAndKey(extras, supplementKey, pax),
              description: supplementRow.description,
              weightText:
                supplementRow.weight != null && supplementRow.unit != null
                  ? `${supplementRow.weight}${supplementRow.unit}`
                  : supplementRow.weight != null
                    ? `${supplementRow.weight}`
                    : "",
              flight: {
                fltRef: supplementRow.fltRef,
                depCity: leg.depCity || "",
                arrCity: leg.arrCity || "",
                flightIndex,
              },
              ourFare: computeOurFareNis(supplementRow.fare, rawItem),
            };
          });
        } else {
          let flightIndex = 0;
          const fltRef = supplementRow.fltRef;
          if (fltRef === "-2" || fltRef === "0" || fltRef === "-1") {
            flightIndex = 0;
          } else if (fltRef === "1") {
            flightIndex = 1;
          } else if (fltRef === "2") {
            flightIndex = 2;
          } else {
            const parsedRef = parseInt(fltRef, 10);
            if (!Number.isNaN(parsedRef) && parsedRef >= 0) {
              flightIndex = parsedRef;
            } else {
              flightIndex = 0;
            }
          }
          const flightNode =
            flights.length > 0 ? flights[flightIndex] ?? flights[0] : { leg: { depCity: "", arrCity: "" } };
          const leg = firstLegFromFlightNode(flightNode) || {};
          suppCount += 1;
          const seqNum = suppCount;
          const supplementKey = computeMockupSupplementKey(
            rawItem,
            flightIndex,
            currentI
          );
          ldsSupplement[seqNum] = {
            "@seqNum": seqNum,
            orig: {
              flight: flightNode.leg,
              supp: suppForOrig,
            },
            pax,
            type: supplementRow.type,
            checked: isCheckedForPaxAndKey(extras, supplementKey, pax),
            description: supplementRow.description,
            weightText:
              supplementRow.weight != null && supplementRow.unit != null
                ? `${supplementRow.weight}${supplementRow.unit}`
                : supplementRow.weight != null
                  ? `${supplementRow.weight}`
                  : "",
            flight: {
              fltRef: supplementRow.fltRef,
              depCity: leg.depCity || "",
              arrCity: leg.arrCity || "",
              flightIndex,
            },
            ourFare: computeOurFareNis(supplementRow.fare, rawItem),
          };
        }
      });
    });
  });

  return ldsSupplement;
}
